import { z } from 'zod';

export const uuidSchema = z.string().uuid();

export const emailSchema = z.string().trim().toLowerCase().min(3).max(254).email();

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRule {
  readonly id: string;
  readonly label: string;
  readonly test: (value: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = Object.freeze([
  {
    id: 'length',
    label: `at least ${String(PASSWORD_MIN_LENGTH)} characters`,
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  { id: 'lowercase', label: 'a lowercase letter', test: (value) => /[a-z]/.test(value) },
  { id: 'uppercase', label: 'an uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { id: 'digit', label: 'a digit', test: (value) => /[0-9]/.test(value) },
  {
    id: 'symbol',
    label: 'a symbol, such as ! ? # or -',
    test: (value) => /[^A-Za-z0-9]/.test(value),
  },
]);

export function failedPasswordRules(value: string): readonly PasswordRule[] {
  return PASSWORD_RULES.filter((rule) => !rule.test(value));
}

export const passwordSchema = z
  .string()
  .max(PASSWORD_MAX_LENGTH)
  .superRefine((value, ctx) => {
    for (const rule of failedPasswordRules(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Your password needs ${rule.label}.` });
    }
  });

export const slugSchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase words separated by single hyphens.');

export const shortTextSchema = z.string().trim().min(1).max(200);
export const richTextSchema = z.string().trim().max(50_000);

export const PAGE_SIZE_DEFAULT = 25;
export const PAGE_SIZE_MAX = 100;

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});

export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export interface PageMeta {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly totalPages: number;
}

export interface Page<TItem> {
  readonly items: readonly TItem[];
  readonly meta: PageMeta;
}

export const sortDirectionSchema = z.enum(['asc', 'desc']).default('desc');
export type SortDirection = z.infer<typeof sortDirectionSchema>;

export const isoDateTimeSchema = z.string().datetime({ offset: true });
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD.');
